import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { BrandingForm, type BrandingDraft } from '@/components/BrandingForm/BrandingForm';
import { Callout } from '@/components/Callout/Callout';
import { Spinner } from '@/components/Spinner/Spinner';
import {
  BRANDING_QUERY,
  SET_TENANT_BRANDING,
  type BrandingResult,
} from '@/graphql/brokerage-settings';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { uploadFile, type UploadProgress } from '@/lib/upload';
import { useToast } from '@/toast/useToast';

import styles from '../BrokeragesContainer.module.css';
import type { SectionProps } from './MembersSection';

/** The name, website and logo the brokerage's emails and the main app carry. */
export function BrandingSection({ tenantId, canEdit, onAccessChanged }: SectionProps) {
  const toast = useToast();
  const client = useApolloClient();
  const branding = useQuery<BrandingResult>(BRANDING_QUERY, {
    variables: { tenantId },
    fetchPolicy: 'cache-and-network',
  });
  const [setBranding] = useMutation(SET_TENANT_BRANDING);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  const onSubmit = async (draft: BrandingDraft) => {
    setBusy(true);
    setError(null);
    try {
      const input: Record<string, unknown> = {
        tenantId,
        companyName: draft.companyName || null,
        website: draft.website || null,
      };
      if (draft.logo === 'remove') {
        input.logoFileId = null;
      } else if (draft.logo) {
        input.logoFileId = (await uploadFile(client, draft.logo, { onProgress: setProgress })).id;
      }
      await setBranding({ variables: { input } });
      await branding.refetch();
      setRevision((n) => n + 1);
      toast.success({ title: 'Branding saved' });
    } catch (cause) {
      setError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        onAccessChanged();
      }
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <section aria-label="Branding" className={styles.section}>
      <p className={styles.sectionLede}>
        What this brokerage’s emails and the main app show. Saved changes reach the main app within
        a minute or two.
      </p>
      {branding.error && !branding.data ? (
        <Callout tone="danger" title="Branding couldn't be loaded">
          {friendlyError(branding.error)}
        </Callout>
      ) : !branding.data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading branding…
        </p>
      ) : (
        <BrandingForm
          branding={branding.data.brokerage}
          busy={busy}
          canEdit={canEdit}
          error={error}
          key={revision}
          onSubmit={onSubmit}
          progress={progress}
        />
      )}
    </section>
  );
}
